import { decodeJwtPayload, isJwtPayloadExpired } from './jwt';

export const PARTNER_SSO_SOURCE = 'partner' as const;
export const LEGACY_PARTNER_SSO_SOURCE = 'web_partner_sso' as const;
export const DEVSTACK_BLR_PULSE_PARTNER_MERCHANT_ID = 'BLRPulseMID01';
const DEVSTACK_FALLBACK_ENVS = new Set(['development', 'devstack']);

export type PartnerSsoSource =
  | typeof PARTNER_SSO_SOURCE
  | typeof LEGACY_PARTNER_SSO_SOURCE;

export type PartnerNotes = Record<string, string>;

export interface BlrPulseSsoJwtClaims extends Record<string, unknown> {
  sub?: string;
  user_id?: string;
  phone?: string;
  partner_merchant_id?: string;
  source?: string;
  notes?: PartnerNotes;
  iat: number;
  exp: number;
}

export interface PartnerSessionMetadata {
  partnerId: string;
  source: PartnerSsoSource;
  notes?: PartnerNotes;
  city?: string;
}

export type PartnerTokenResult =
  | {
      status: 'partner';
      token: string;
      claims: BlrPulseSsoJwtClaims;
      partnerSession: PartnerSessionMetadata;
    }
  | {
      status: 'non_partner';
      token: string;
      claims: BlrPulseSsoJwtClaims;
    }
  | {
      status: 'invalid' | 'expired';
      token: string;
    };

export interface PartnerSsoLaunchContext {
  city?: string;
}

const isStringRecord = (value: unknown): value is PartnerNotes => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every((entry) => typeof entry === 'string');
};

const normalizeNotes = (notes: unknown): PartnerNotes | undefined => {
  return isStringRecord(notes) ? notes : undefined;
};

const isDevstackPartnerFallbackEnabled = (): boolean => {
  const appEnv = process.env.VITE_APP_ENV || process.env.NODE_ENV || '';
  return DEVSTACK_FALLBACK_ENVS.has(appEnv);
};

const getPartnerMerchantId = (
  claims: BlrPulseSsoJwtClaims,
): string | undefined => {
  const partnerMerchantId = claims.partner_merchant_id?.trim();

  if (partnerMerchantId) {
    return partnerMerchantId;
  }

  // TODO(BLR Pulse): remove this devstack fallback once OAuth integration
  // populates partner_merchant_id in the SSO JWT.
  return isDevstackPartnerFallbackEnabled()
    ? DEVSTACK_BLR_PULSE_PARTNER_MERCHANT_ID
    : undefined;
};

const isPartnerSsoSource = (
  source: string | undefined,
): source is PartnerSsoSource => {
  return source === PARTNER_SSO_SOURCE || source === LEGACY_PARTNER_SSO_SOURCE;
};

export const parsePartnerSsoToken = (
  token: string,
  context: PartnerSsoLaunchContext = {},
): PartnerTokenResult => {
  const claims = decodeJwtPayload<BlrPulseSsoJwtClaims>(token);

  if (!claims) {
    return { status: 'invalid', token };
  }

  if (isJwtPayloadExpired(claims)) {
    return { status: 'expired', token };
  }

  if (!isPartnerSsoSource(claims.source)) {
    return { status: 'non_partner', token, claims };
  }

  const partnerMerchantId = getPartnerMerchantId(claims);

  if (!partnerMerchantId) {
    return { status: 'non_partner', token, claims };
  }

  return {
    status: 'partner',
    token,
    claims,
    partnerSession: {
      partnerId: partnerMerchantId,
      source: claims.source,
      city: context.city,
      notes: normalizeNotes(claims.notes),
    },
  };
};

const getSearchParam = (href: string, key: string): string | null => {
  try {
    const value = new URL(href).searchParams.get(key)?.trim();
    return value || null;
  } catch {
    return null;
  }
};

export const getSsoTokenFromUrl = (href: string): string | null =>
  getSearchParam(href, 'token');

export const getSsoCodeFromUrl = (href: string): string | null =>
  getSearchParam(href, 'code');

export const getSsoCityFromUrl = (href: string): string | undefined =>
  getSearchParam(href, 'city') ?? undefined;

export const stripTokenFromUrl = (href: string): string => {
  try {
    const url = new URL(href);
    url.searchParams.delete('code');
    url.searchParams.delete('token');
    const noteParamKeys: string[] = [];
    url.searchParams.forEach((_, key) => {
      if (key === 'notes' || key.startsWith('notes[')) {
        noteParamKeys.push(key);
      }
    });
    noteParamKeys.forEach((key) => url.searchParams.delete(key));

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
};
